import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useEffectEvent, useRef } from "react";
const getLeaflet = () => {
    return window.L;
};
const getHeatmapPoints = (points) => {
    return points.map((point) => [Number(point.Latitude), Number(point.Longitude), Number(point.Value)]);
};
export const LeafletHeatmap = ({ id, options, data, locations, params, onViewportChange, className, style }) => {
    const mapElementRef = useRef(null);
    const mapRef = useRef(null);
    const heatmapLayerRef = useRef(null);
    const locationMarkersRef = useRef(null);
    const locationsRef = useRef(locations);
    const paramsRef = useRef(params);
    const optionsRef = useRef(options);
    const iconsRef = useRef(null);
    const reportViewport = useEffectEvent(() => {
        const map = mapRef.current;
        if (!map) {
            return;
        }
        const bounds = map.getBounds();
        onViewportChange({
            northWest: bounds.getNorthWest(),
            southEast: bounds.getSouthEast(),
            zoomLevel: map.getZoom(),
        });
    });
    const removeMarkers = useEffectEvent(() => {
        const map = mapRef.current;
        const locationMarkers = locationMarkersRef.current;
        if (!map || !locationMarkers || !map.hasLayer(locationMarkers)) {
            return;
        }
        map.removeLayer(locationMarkers);
        locationMarkers.clearLayers();
    });
    const handleMarkerClick = useEffectEvent((event) => {
        const markerId = event.target.ID;
        const currentParams = paramsRef.current;
        if (markerId === undefined || currentParams === null) {
            return;
        }
        const link = `${optionsRef.current.locationLinkTo}?${new URLSearchParams({
            fromMonth: currentParams.startDate.substring(0, 7),
            toMonth: currentParams.endDate.substring(0, 7),
            vesselTypes: currentParams.vesselTypes.join(),
            fleets: currentParams.fleet.map((fleet) => fleet.ID).join(),
            locationId: String(markerId),
            isIndustryMode: String(currentParams.isIndustryMode),
        }).toString()}`;
        window.location.href = link;
    });
    const updateMapMarkers = useEffectEvent(() => {
        const leaflet = getLeaflet();
        const map = mapRef.current;
        const locationMarkers = locationMarkersRef.current;
        const icons = iconsRef.current;
        if (!leaflet || !map || !locationMarkers || !icons) {
            return;
        }
        removeMarkers();
        const zoom = map.getZoom();
        if (zoom < optionsRef.current.markerMinZoomLevel) {
            return;
        }
        const bounds = map.getBounds();
        const southWest = bounds.getSouthWest();
        const northEast = bounds.getNorthEast();
        const visibleLocations = locationsRef.current.filter((location) => {
            return (location[1] >= southWest.lat &&
                location[1] <= northEast.lat &&
                location[2] >= southWest.lng &&
                location[2] <= northEast.lng);
        });
        visibleLocations.forEach((location) => {
            const performanceLevel = location[6];
            const markerIcon = performanceLevel === "Good"
                ? icons.green
                : performanceLevel === "Medium"
                    ? icons.yellow
                    : performanceLevel === "Bad"
                        ? icons.red
                        : icons.blue;
            const marker = new leaflet.marker([location[1], location[2]], {
                title: location[0],
                icon: markerIcon,
            });
            marker.ID = location[3];
            marker.on("click", handleMarkerClick);
            locationMarkers.addLayer(marker);
        });
        if (zoom >= optionsRef.current.zoneMinZoomLevel) {
            visibleLocations.forEach((location) => {
                if (location[5] === null) {
                    locationMarkers.addLayer(leaflet.circle([location[1], location[2]], {
                        radius: location[4] * 1852,
                    }));
                    return;
                }
                locationMarkers.addLayer(leaflet.polygon(location[5]));
            });
        }
        map.addLayer(locationMarkers);
    });
    useEffect(() => {
        locationsRef.current = locations;
        paramsRef.current = params;
    }, [locations, params]);
    useEffect(() => {
        const leaflet = getLeaflet();
        const mapElement = mapElementRef.current;
        if (!leaflet) {
            console.error("Leaflet not installed.");
            return;
        }
        if (!mapElement) {
            return;
        }
        const LeafIcon = leaflet.Icon.extend({
            options: {
                shadowUrl: "/scripts/libs/images/marker-shadow.png",
                iconSize: [25, 41],
                iconAnchor: [12, 41],
                popupAnchor: [1, -34],
                shadowSize: [41, 41],
            },
        });
        iconsRef.current = {
            green: new LeafIcon({ iconUrl: "/image/heatmap/marker-icon-green.png" }),
            red: new LeafIcon({ iconUrl: "/image/heatmap/marker-icon-red.png" }),
            orange: new LeafIcon({ iconUrl: "/image/heatmap/marker-icon-orange.png" }),
            yellow: new LeafIcon({ iconUrl: "/image/heatmap/marker-icon-yellow.png" }),
            blue: new LeafIcon({ iconUrl: "/image/heatmap/marker-icon-blue.png" }),
        };
        const { initialZoomLevel, initialCenter, markerMinZoomLevel: _markerMinZoomLevel, zoneMinZoomLevel: _zoneMinZoomLevel, showLocations: _showLocations, showMarkersOnlyOnHover: _showMarkersOnlyOnHover, locationLinkTo: _locationLinkTo, zoomControl, scrollWheelZoom, ...leafletOptions } = optionsRef.current;
        const defaultMapOptions = {
            zoomControl: false,
            scrollWheelZoom: false,
            zoom: initialZoomLevel,
            center: initialCenter,
        };
        const map = leaflet.map(mapElement, { ...defaultMapOptions, ...leafletOptions, zoomControl, scrollWheelZoom });
        mapRef.current = map;
        locationMarkersRef.current = new leaflet.FeatureGroup();
        map.whenReady(reportViewport);
        map.on("moveend", (event) => {
            if (!event?.hard) {
                reportViewport();
            }
        });
        map.on("zoomend", (event) => {
            if (!event?.hard) {
                reportViewport();
                if (optionsRef.current.showLocations) {
                    updateMapMarkers();
                }
            }
        });
        const layerBaseMap = leaflet.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}", {
            attribution: "Powered by Esri",
            maxZoom: 13,
            noWrap: false,
        });
        const seaLayer = leaflet.tileLayer("http://tiles.openseamap.org/seamark/{z}/{x}/{y}.png", {
            maxZoom: 13,
            attribution: '&copy; <a href="http://www.openseamap.org">OpenSeaMap</a> contributors',
            noWrap: false,
        });
        if (map.attributionControl) {
            map.attributionControl.setPrefix(false);
        }
        map.addLayer(layerBaseMap);
        map.addLayer(seaLayer);
        leaflet.control.scale().addTo(map);
        if (map.zoomControl) {
            map.zoomControl.setPosition("topright");
        }
        requestAnimationFrame(() => {
            map.invalidateSize({ pan: false, debounceMoveend: true });
        });
        if (optionsRef.current.showLocations) {
            map.on("dragend", updateMapMarkers);
            if (optionsRef.current.showMarkersOnlyOnHover) {
                map.on("mouseover", updateMapMarkers);
                map.on("mouseout", removeMarkers);
            }
        }
        return () => {
            map.remove();
            mapRef.current = null;
            heatmapLayerRef.current = null;
            locationMarkersRef.current = null;
        };
    }, []);
    useEffect(() => {
        const leaflet = getLeaflet();
        const map = mapRef.current;
        if (!leaflet || !map) {
            return;
        }
        const points = getHeatmapPoints(data);
        // Omit `max` so Leaflet.heat defaults to 1, keeping per-point opacity high.
        // Explicitly setting max to the dataset's max value causes most points to render
        // at minOpacity (0.05) because of the zoom-level intensity scaling factor.
        const layerOptions = {
            radius: 5,
            maxZoom: 4,
            blur: 10,
        };
        if (heatmapLayerRef.current) {
            heatmapLayerRef.current.setOptions(layerOptions);
            heatmapLayerRef.current.setLatLngs(points);
        }
        else {
            heatmapLayerRef.current = leaflet.heatLayer(points, layerOptions).addTo(map);
        }
        if (!optionsRef.current.dragging) {
            map.setView(optionsRef.current.initialCenter);
        }
    }, [data]);
    useEffect(() => {
        if (optionsRef.current.showLocations) {
            updateMapMarkers();
        }
    }, [locations, updateMapMarkers]);
    return _jsx("div", { id: id, ref: mapElementRef, className: className, style: style });
};
